import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Eye, CopyPlus } from "lucide-react";
import UpgradeModal from "@/components/UpgradeModal";
import { api, imgUrl, formatErr } from "@/lib/api";
import { STEPS, toPayload } from "@/components/muse/steps";
import IdentityLock from "@/components/muse/IdentityLock";

export default function MuseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [upsell, setUpsell] = useState(false);
  const duplicate = async () => {
    try {
      const { data } = await api.post(`/muses/${id}/duplicate`);
      toast.success(`${data.name} added to your muses`);
      nav(`/muses/${data.id}`);
    } catch (err) {
      if (err?.response?.data?.detail === "MUSE_LIMIT_REACHED") setUpsell(true);
      else toast.error(formatErr(err));
    }
  };

  useEffect(() => {
    api.get(`/muses/${id}`).then(({ data }) => setM(data)).catch(() => nav("/muses"));
  }, [id, nav]);

  if (!m) return <p className="muted text-sm">Loading…</p>;
  const editing = !!draft;
  const view = draft || m;
  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!draft.name.trim() || !draft.look_description.trim()) return toast.error("Name and look description are required.");
    setBusy(true);
    try {
      const { data } = await api.put(`/muses/${id}`, toPayload(draft));
      setM(data); setDraft(null);
      toast.success("Saved — Identity Lock updated");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const img = imgUrl(m.face_reference_url);
  return (
    <div className="pb-8">
      <Link to="/muses" className="text-xs uppercase tracking-[0.15em] muted flex items-center gap-2 mb-8 hover:text-[#1A1325]" data-testid="muse-detail-back-link">
        <ArrowLeft size={14} /> Muses
      </Link>
      <header className="flex flex-col sm:flex-row gap-6 sm:items-end mb-10 fade-up">
        <div className="w-32 h-40 sm:w-40 sm:h-52 rounded-[16px] overflow-hidden border border-[#F0EAF7] bg-[#FAF7FE] shrink-0">
          {img && <img src={img} alt={m.name} className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          {m.is_example && <p className="eyebrow mb-2" data-testid="muse-detail-example-label">Example muse · read-only</p>}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl" data-testid="muse-detail-name">{m.name}</h1>
          <p className="muted text-sm mt-2">{m.niche}</p>
        </div>
        {m.is_example && (
          <button className="btn-primary" onClick={duplicate} data-testid="muse-detail-duplicate-button"><CopyPlus size={14} /> Duplicate to my muses</button>
        )}
        {!m.is_example && (
          <div className="flex gap-2">
            {m.wizard_step < 5 && !editing && (
              <Link to={`/muses/${id}/build?step=${m.wizard_step}`} className="btn-secondary" data-testid="muse-detail-continue-wizard-button">Continue setup</Link>
            )}
            {editing ? (
              <button className="btn-secondary" onClick={() => setDraft(null)} data-testid="muse-detail-cancel-button"><Eye size={14} /> Cancel</button>
            ) : (
              <button className="btn-primary" onClick={() => setDraft(m)} data-testid="muse-detail-edit-button"><Pencil size={14} /> Edit</button>
            )}
          </div>
        )}
      </header>

      <IdentityLock text={m.identity_lock} />

      <div className="mt-12 space-y-12">
        {STEPS.map(({ title, sub, C }, i) => (
          <section key={title} className="md:grid md:grid-cols-[200px_1fr] gap-10" data-testid={`muse-section-${i + 1}`}>
            <div className="mb-5 md:mb-0">
              <p className="eyebrow">0{i + 1}</p>
              <h2 className="text-base md:text-lg font-display mt-1">{title}</h2>
              <p className="muted text-xs mt-1">{sub}</p>
            </div>
            <div className="panel p-5 md:p-7"><C m={view} set={set} ro={!editing} /></div>
          </section>
        ))}
      </div>

      {editing && (
        <div className="sticky bottom-20 md:bottom-6 mt-10 z-30">
          <button className="btn-primary w-full py-4" onClick={save} disabled={busy} data-testid="muse-detail-save-button">{busy ? "Saving…" : "Save changes"}</button>
        </div>
      )}
      <UpgradeModal open={upsell} onOpenChange={setUpsell} />
    </div>
  );
}
